import React, { useCallback, useMemo, useEffect } from 'react'
import { useFormContext, Controller } from 'react-hook-form'

import { TextField, TextFieldProps } from '@mui/material'
import { ITimeRange } from 'types/components/timePicker'

import { debounce } from 'common/util/debounce'

import MaskedField from '../MaskedField/index'

interface IProps {
  name: string
  placeholder?: string
  slots?: string
  fieldProps?: TextFieldProps
  [key: string]: any
}

type TimeRangeStringValue = `${number}:${number} - ${number}:${number}`

export default function RHFTimeRangeField({
  name,
  placeholder = '__:__ - __:__',
  slots = '_',
  fieldProps,
  ...other
}: IProps) {
  const { control, setValue, watch } = useFormContext()

  const transformValue = useCallback((value?: TimeRangeStringValue) => {
    const validRegex = /\d{0,2}:\d{0,2} - \d{0,2}:\d{0,2}/
    if (!value || value.includes('_') || !validRegex.test(value)) return null

    const [start, end] = value.split(' - ')
    const [startHours, startMinutes] = start.split(':')
    const [endHours, endMinutes] = end.split(':')

    return { startTime: `${startHours}:${startMinutes}`, endTime: `${endHours}:${endMinutes}` }
  }, [])

  const maskedValue = useMemo(() => {
    const value = watch(name) as ITimeRange
    if (!value) return ''

    const { startTime, endTime } = value
    return `${startTime} - ${endTime}`
  }, [watch, name, watch(name)])

  const handleChange = useCallback(
    (value: TimeRangeStringValue) => {
      setValue(name, transformValue(value), { shouldValidate: true, shouldDirty: true })
    },
    [transformValue, setValue],
  )

  const debouncedHandleChange = useMemo(() => debounce(handleChange, 750), [])

  // fire onChange when value is changed from outside the component (on reset)
  useEffect(() => {
    const value = watch(name) as ITimeRange

    if (value && !Object.values(value).length) {
      const { startTime, endTime } = value as ITimeRange
      const timeRangeStringValue: TimeRangeStringValue = `${startTime} - ${endTime}`
      handleChange(timeRangeStringValue)
    }
  }, [watch, name, handleChange])

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <MaskedField
          placeholder={placeholder}
          slots={slots}
          component={TextField}
          onFinish={debouncedHandleChange as (value: string) => void}
          outsideValue={maskedValue}
          componentProps={{
            ...other,
            fullWidth: true,
            error: !!error,
            helperText: error?.message,
            ...fieldProps,
            ...field,
            placeholder,
            InputProps: {
              ...field,
              onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                debouncedHandleChange(event.target.value as TimeRangeStringValue)
              },
            },
          }}
        />
      )}
    />
  )
}
